import { useMemo } from "react";
import { geoMercator, geoPath } from "d3-geo";
import { useTranslation } from "react-i18next";

import { AmbientMapLegend } from "../Helpers/MapLegend";
import "./ImageAmbientForm.scss";

const classColorKeys = [
  "mbp",
  "bp",
  "pp",
  "ap",
  "map",
  "five",
  "six",
];

const getColor = (key, numClasses) => {
  const colors = {
    2: {
      mbp: "rgb(215,25,28)",
      bp: "rgb(26,150,65)",
    },
    3: {
      mbp: "rgb(215,25,28)",
      bp: "rgb(255,255,192)",
      pp: "rgb(26,150,65)",
    },
    4: {
      mbp: "rgb(215,25,28)",
      bp: "rgb(254,201,129)",
      pp: "rgb(196,230,135)",
      ap: "rgb(26,150,65)",
    },
    5: {
      mbp: "rgb(215,25,28)",
      bp: "rgb(253,174,97)",
      pp: "rgb(255,255,192)",
      ap: "rgb(166,217,106)",
      map: "rgb(26,150,65)",
    },
    6: {
      mbp: "rgb(215,25,28)",
      bp: "rgb(246,144,83)",
      pp: "rgb(255,223,154)",
      ap: "rgb(220,240,158)",
      map: "rgb(138,204,98)",
      five: "rgb(26,150,65)",
    },
    7: {
      mbp: "rgb(215,25,28)",
      bp: "rgb(241,124,74)",
      pp: "rgb(254,201,129)",
      ap: "rgb(255,255,192)",
      map: "rgb(196,230,135)",
      five: "rgb(119,195,92)",
      six: "rgb(26,150,65)",
    },
  }[parseInt(numClasses, 10)];

  return colors?.[key] || "transparent";
};

const getPreviewColor = (classIndex, numClasses) => {
  return getColor(classColorKeys[Number(classIndex)], numClasses);
};

const ImageAmbientForm = ({ numClasses, geojson_preview, layerDate }) => {
  const { t } = useTranslation();
  const previewFeatures = useMemo(() => {
    return Array.isArray(geojson_preview?.features) ? geojson_preview.features : [];
  }, [geojson_preview]);
  const previewAmbient = useMemo(() => ({
    ambient_id: "image-ambient-preview",
    params: {
      breaks: numClasses,
      ...(layerDate ? { layer_date: layerDate } : {}),
    },
    geojson: {
      ...(geojson_preview || {}),
      type: geojson_preview?.type || "FeatureCollection",
      features: previewFeatures,
    },
  }), [geojson_preview, layerDate, numClasses, previewFeatures]);

  const projection = useMemo(() => {
    return geoMercator().fitSize([400, 400], geojson_preview);
  }, [geojson_preview]);

  const pathGenerator = useMemo(() => {
    return geoPath().projection(projection);
  }, [projection]);

  return (
    <div className="image-ambient-form">
      <div className="image-ambient-form__hero">
        <div className="image-ambient-form__preview-card">
          <span className="image-ambient-form__summary-label">{t("img_ambient:creation.preview.label")}</span>
          <p className="image-ambient-form__preview-description">
            {t("img_ambient:creation.preview.description")}
          </p>
          <div className="image-ambient-form__preview-frame">
            <svg width="100%" height="280" viewBox="0 0 400 400" className="image-ambient-form__preview" aria-label={t("img_ambient:creation.preview.aria_label")}>
              {previewFeatures.map((feature, index) => (
                <path
                  key={index}
                  d={pathGenerator(feature)}
                  fill={getPreviewColor(feature.properties.cls, numClasses)}
                />
              ))}
            </svg>
          </div>
        </div>

        <AmbientMapLegend
          ambient={previewAmbient}
          className="gis-map-legend--modal image-ambient-form__legend"
        />
      </div>
    </div>
  );
};

export default ImageAmbientForm;
